/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx shared component; this is an additive submenu-heading change, a .tsx migration is separate */
/* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-unused-expressions, no-constant-binary-expression, react-hooks/preserve-manual-memoization -- pre-existing issues in this legacy component; out of scope for an additive change */
import { KText } from '@/ui-library/components/text';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Tooltip,
  useDisclosure,
  Menu as ChakraMenu,
  Spinner,
} from '@chakra-ui/react';
import { ChevronRight, DotsHorizontal } from '@kula-ai/icons-core';
import React, {
  isValidElement,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useEnableHeaderFooterBorder } from '@/hooks';

export const TableRowActions = (props) => {
  const {
    actionItems = [],
    className,
    actionComp: ActionComp,
    row: { original: selected = {}, isRowDisabled } = {},
    toggleAllRowsSelected = () => {},
    rowActionMenuStyle = {},
  } = props;

  const actionRef = useRef(null);
  const modalBodyRef = useRef(null);
  const [currentAction, setCurrentAction] = useState();
  const modalActions = useDisclosure();
  const { isOpen, onOpen, onClose } = modalActions;
  const menuActions = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState(null);

  const { hasOverflow } = useEnableHeaderFooterBorder(modalBodyRef);

  // Find and highlight parent row when menu opens
  useEffect(() => {
    const findParentRow = () => {
      let element = actionRef.current;
      while (element && element.tagName !== 'TR') {
        element = element.parentElement;
      }
      return element;
    };

    // find the table container, we are using this class name there "tableLayout"
    // based on the class, attach scroll event and close the menu on scroll
    const findTableContainer = () => {
      let element = actionRef.current;
      while (element && !element.classList.contains('tableLayout')) {
        element = element.parentElement;
      }
      return element?.parentElement; // Get the scrollable container
    };

    const parentRow = findParentRow();
    const tableContainer = findTableContainer();

    // Close menu on table scroll
    const handleScroll = () => {
      if (menuActions?.isOpen) {
        menuActions?.onClose();
        setOpenSubMenuIndex(null);
      }
    };

    if (menuActions?.isOpen && tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
    }

    if (parentRow) {
      if (menuActions.isOpen) {
        parentRow.style.background = '#f4f4fb';
        parentRow.classList.add('menu-open-highlight');
      } else {
        parentRow.style.background = '';
        parentRow.classList.remove('menu-open-highlight');
        setOpenSubMenuIndex(null); // Close submenu when main menu closes
      }
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScroll);
      }
      if (parentRow) {
        parentRow.style.background = '';
        parentRow.classList.remove('menu-open-highlight');
      }
    };
  }, [menuActions]);

  const { mainActions, subActions } = actionItems.reduce(
    (acc, obj) => {
      obj?.type === 'mainAction'
        ? acc.mainActions.push(obj)
        : acc.subActions.push(obj);
      return acc;
    },
    { mainActions: [], subActions: [] }
  );

  const renderItem = (item) => {
    const {
      label,
      setLabel,
      color,
      labelProps,
      tooltip,
      description,
      additionalInfo,
    } = item || {};
    let finalLabel = label;
    if (setLabel) {
      finalLabel = setLabel(selected);
    }

    let finalTooltip = tooltip;
    if (typeof tooltip === 'function') {
      finalTooltip = tooltip(selected);
    }

    let finalAdditionalInfo = null;
    if (typeof additionalInfo === 'function') {
      finalAdditionalInfo = additionalInfo(selected);
    } else if (additionalInfo) {
      finalAdditionalInfo = additionalInfo;
    }

    // Check if this item is loading
    const itemIsLoading =
      typeof item?.isLoading === 'function'
        ? item.isLoading(selected)
        : item?.isLoading;

    // If description or additionalInfo is provided, show extended layout
    if (description || finalAdditionalInfo) {
      return (
        <Flex
          key={item?.id}
          gap="12px"
          alignItems="flex-start"
          w="350px"
          data-test-id={item?.id}
        >
          <Flex direction="column" gap="4px" flex="1">
            <Flex gap="6px" alignItems="center">
              {itemIsLoading ? (
                <Spinner size="sm" color="primary-500" />
              ) : (
                item?.icon &&
                !item?.iconAlignRight && (
                  <Icon
                    as={item?.icon}
                    {...item?.iconProps}
                    mt="2px"
                    flexShrink={0}
                  />
                )
              )}
              {finalLabel && (
                <KText
                  size="sm-medium"
                  color={labelProps?.color || color || 'secondary-900'}
                  {...labelProps}
                >
                  {finalLabel}
                </KText>
              )}

              {item?.icon && item?.iconAlignRight && !itemIsLoading && (
                <Icon as={item?.icon} {...item?.iconProps} />
              )}
            </Flex>
            {description && (
              <KText size="sm-regular" color="secondary-600">
                {description}
              </KText>
            )}
            {finalAdditionalInfo ? finalAdditionalInfo : null}
          </Flex>
        </Flex>
      );
    }

    // Default rendering with tooltip (for backwards compatibility)
    return (
      <Tooltip variant="default" label={finalTooltip}>
        <Flex
          key={item?.id}
          gap="6px"
          alignItems="center"
          data-test-id={item?.id}
        >
          {itemIsLoading ? (
            <Spinner size="sm" color="primary-500" />
          ) : (
            item?.icon &&
            !item?.iconAlignRight && (
              <Icon as={item?.icon} {...item?.iconProps} />
            )
          )}
          {finalLabel && (
            // To change fontweight on hover here also
            <KText
              size="sm-medium"
              color={labelProps?.color || color || 'secondary-700'}
              {...labelProps}
            >
              {' '}
              {finalLabel}
            </KText>
          )}
          {item?.icon && item?.iconAlignRight && !itemIsLoading && (
            <Icon as={item?.icon} {...item?.iconProps} />
          )}
        </Flex>
      </Tooltip>
    );
  };

  // Helper function to evaluate enabled property
  const isItemEnabled = useCallback(
    (item) => {
      if (typeof item?.enabled === 'function') {
        return item.enabled(selected);
      }
      return Boolean(item?.enabled);
    },
    [selected]
  );

  const validSubActions = useMemo(() => {
    return (
      subActions?.filter(
        (config) =>
          isItemEnabled(config) &&
          ((config?.isConditional && config?.checkCondition(selected)) ||
            !config?.isConditional)
      ) || []
    );
  }, [subActions, selected, isItemEnabled]);

  const validMainActions = useMemo(() => {
    return (
      mainActions?.filter(
        (config) =>
          isItemEnabled(config) &&
          ((config?.isConditional && config?.checkCondition(selected)) ||
            !config?.isConditional)
      ) || []
    );
  }, [mainActions, selected, isItemEnabled]);
  const renderAction = () => {
    return (
      <Menu
        isOpen={menuActions?.isOpen}
        onClose={menuActions?.onClose}
        isLazy={true}
        gutter={4}
        strategy="fixed"
        placement="bottom-end"
      >
        <Flex
          ref={actionRef}
          border={!ActionComp && '1px #E4E4E7 solid'}
          boxShadow={!ActionComp && '0px 1px 5px rgba(0, 0, 0, 0.05)'}
          bg={!ActionComp && '#fff'}
          borderRadius={!ActionComp && '8px'}
          {...rowActionMenuStyle}
          position="relative"
        >
          {validMainActions?.map((item) => {
            const itemDisabled = item?.isDisabled?.(selected);
            return item?.comp ? (
              <item.comp key={item.id} {...item?.props} />
            ) : (
              <Box
                color={item?.color || undefined}
                onClick={() => {
                  if (item?.onClick) {
                    item?.onClick(onClose, selected, setIsLoading);
                  } else {
                    setCurrentAction(item);
                    onOpen();
                  }
                }}
                key={item?.id}
                cursor={itemDisabled ? 'not-allowed' : 'pointer'}
                p="8px"
                borderRight={
                  rowActionMenuStyle?.border === 'none !important'
                    ? 'none'
                    : '1px #E4E4E7 solid'
                }
                opacity={itemDisabled ? 0.5 : 1}
              >
                {renderItem(item)}
              </Box>
            );
          })}
          {validSubActions.length > 0 && (
            <MenuButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (menuActions?.isOpen) {
                  menuActions?.onClose();
                } else {
                  menuActions?.onOpen();
                }
              }}
            >
              {ActionComp ? (
                <ActionComp />
              ) : (
                <Flex
                  width="30px"
                  height="30px"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={DotsHorizontal}
                    color="secondary-700"
                    width="18px"
                    height="18px"
                    strokeWidth="1.2"
                    data-test-id="table-row-actions-more-menu-button"
                  />
                </Flex>
              )}
            </MenuButton>
          )}
        </Flex>
        {validSubActions.length > 0 && (
          <Portal>
            {/* using this in drawer and drawer has default of 1400 z index */}
            <MenuList
              zIndex="1500"
              minW="fit-content"
              maxW="360px"
              sx={{
                '& > *': {
                  transition: 'background 0.2s',
                },
              }}
              borderRadius="6px"
              p="0"
            >
              {validSubActions.map((config, index) => {
                const itemDisabled = config?.isDisabled?.(selected);

                // Evaluate subItems if it's a function
                let subItemsArray;
                if (typeof config?.subItems === 'function') {
                  subItemsArray = config.subItems(selected);
                } else if (Array.isArray(config?.subItems)) {
                  subItemsArray = config.subItems;
                }

                const hasSubItems =
                  Array.isArray(subItemsArray) && subItemsArray.length > 0;

                // Render nested menu if subItems exist
                if (hasSubItems) {
                  return (
                    <ChakraMenu
                      key={config?.id}
                      placement="right-start"
                      isOpen={openSubMenuIndex === index}
                      onClose={() => setOpenSubMenuIndex(null)}
                      isLazy={true}
                      gutter={4}
                      closeOnSelect={false}
                      strategy="fixed"
                    >
                      <MenuButton
                        as={Box}
                        w="100%"
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ fontWeight: 'semibold', bg: 'secondary-50' }}
                        _focus={{ boxShadow: 'none' }}
                        borderBottom={
                          index !== validSubActions.length - 1
                            ? '1px solid'
                            : undefined
                        }
                        p="12px"
                        borderColor={
                          index !== validSubActions.length - 1
                            ? 'border-color'
                            : undefined
                        }
                        borderRadius="0"
                        color={config?.color || 'secondary-700'}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenSubMenuIndex(
                            openSubMenuIndex === index ? null : index
                          );
                        }}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          gap="6px"
                        >
                          <Flex alignItems="center" gap="6px">
                            {config?.icon && (
                              <Icon
                                as={config?.icon}
                                {...config?.iconProps}
                                flexShrink={0}
                              />
                            )}
                            <KText
                              size="sm-medium"
                              color={config?.color || 'secondary-700'}
                            >
                              {typeof config?.label === 'function'
                                ? config.label(selected)
                                : config?.setLabel
                                  ? config.setLabel(selected)
                                  : config?.label}
                            </KText>
                          </Flex>
                          <ChevronRight
                            size={14}
                            color="var(--kula-colors-secondary-700)"
                            className="kula-icon"
                          />
                        </Flex>
                      </MenuButton>
                      <Portal>
                        <MenuList
                          zIndex="1500"
                          minW="fit-content"
                          maxW="500px"
                          borderRadius="6px"
                          p="0"
                        >
                          {(() => {
                            // Optional non-interactive heading atop the submenu
                            // (e.g. the consent status), set via config.subMenuHeading.
                            // Renders the supplied node as-is so it keeps its own
                            // icon/colour styling.
                            const heading =
                              typeof config?.subMenuHeading === 'function'
                                ? config.subMenuHeading(selected)
                                : config?.subMenuHeading;
                            return heading ? (
                              <Box
                                px="12px"
                                py="8px"
                                borderBottom="1px solid"
                                borderColor="border-color"
                              >
                                <KText size="sm-medium">{heading}</KText>
                              </Box>
                            ) : null;
                          })()}
                          {subItemsArray.map((subItem, subIndex) => {
                            const subItemDisabled =
                              subItem?.isDisabled?.(selected);
                            return (
                              <MenuItem
                                key={subItem?.id}
                                color={subItem?.color || 'secondary-700'}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenSubMenuIndex(null);
                                  if (subItem?.onClick) {
                                    subItem?.onClick(
                                      onClose,
                                      selected,
                                      setIsLoading
                                    );
                                  } else {
                                    setCurrentAction(subItem);
                                    onOpen();
                                  }
                                }}
                                isDisabled={subItemDisabled}
                                _hover={{
                                  fontWeight: 'semibold',
                                  bg: 'secondary-50',
                                }}
                                borderBottom={
                                  subIndex !== subItemsArray.length - 1 &&
                                  '1px solid'
                                }
                                p="12px !important"
                                borderColor={
                                  subIndex !== subItemsArray.length - 1 &&
                                  'border-color'
                                }
                                borderRadius="0"
                                role="group"
                                className="group"
                              >
                                {renderItem(subItem)}
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </Portal>
                    </ChakraMenu>
                  );
                }

                // Regular menu item without subItems
                return (
                  <MenuItem
                    color={config?.color || 'secondary-700' || undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      if (config?.onClick) {
                        config?.onClick(onClose, selected, setIsLoading);
                      } else {
                        setCurrentAction(config);
                        onOpen();
                      }
                    }}
                    key={config?.id}
                    isDisabled={
                      (isRowDisabled && config?.menuToDisable) ||
                      (config?.isCurrUserCheck && isRowDisabled) ||
                      itemDisabled
                    }
                    py={config?.description ? '12px' : undefined}
                    px={config?.description ? '16px' : undefined}
                    _hover={{ fontWeight: 'semibold', bg: 'secondary-50' }}
                    borderBottom={
                      index !== validSubActions.length - 1 && '1px solid'
                    }
                    p="12px !important"
                    borderColor={
                      index !== validSubActions.length - 1 && 'border-color'
                    }
                    borderRadius="0"
                    role="group"
                    className="group"
                  >
                    {renderItem(config)}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Portal>
        )}
      </Menu>
    );
  };

  const handleOnSuccessClose = () => {
    onClose();
    toggleAllRowsSelected(false);
  };

  const {
    title,
    description,
    successButtonText,
    cancelButtonText = 'Cancel',
    loadingText,
    isActionLoading = false,
    handleOnClose = () => {},
    isModalActionLoading = false,
    isButtonDisabled,
    onModalSubmit = () => {},
    RenderContent,
    isCustomModal = false,
  } = currentAction?.modalConfig || {};

  const content = useMemo(() => {
    const customProps = { ...props, modalActions: modalActions, selected };
    if (isValidElement(RenderContent)) {
      return React.cloneElement(RenderContent, customProps);
    }
    if (typeof RenderContent === 'function') {
      return <RenderContent {...customProps} />;
    }
    return null;
  }, [RenderContent, modalActions, props, selected]);

  const renderModal = () => {
    return (
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => handleOnClose(onClose)}
        autoFocus={false}
      >
        <ModalOverlay />
        <ModalContent ref={modalBodyRef}>
          <ModalHeader
            pb={hasOverflow ? '1em' : '0em'}
            borderBottom={hasOverflow ? '1px solid ' : 'none'}
            borderColor={hasOverflow ? 'modal-header-footer-border' : 'none'}
          >
            <Flex flexDir="column" gap=".5em">
              <KText variant="heading_2">{title}</KText>
              {description && <KText>{description}</KText>}
            </Flex>
          </ModalHeader>
          <ModalBody>{content} </ModalBody>
          <ModalFooter
            borderTop={hasOverflow ? '1px solid ' : 'none'}
            borderColor={hasOverflow ? 'modal-header-footer-border' : 'none'}
          >
            <Button
              variant="secondary"
              mr={3}
              onClick={() => handleOnClose(onClose)}
            >
              {cancelButtonText}
            </Button>
            <Button
              onClick={() =>
                onModalSubmit(selected, handleOnSuccessClose, setIsLoading)
              }
              loadingText={loadingText}
              disabled={isButtonDisabled || isActionLoading}
              isLoading={isModalActionLoading || isLoading}
              variant="primary"
            >
              {successButtonText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <Box className={className}>
      {renderAction()}
      {isOpen && isCustomModal ? content : renderModal()}
    </Box>
  );
};
